import React, { useState } from 'react';
import { Modal, Form, Input, Button, Space } from 'antd';
import { authAPI } from '../services/api';

type View = 'form' | 'complete';

interface ForgotPasswordFormValues {
  email: string;
}

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ open, onClose }) => {
  const [form] = Form.useForm<ForgotPasswordFormValues>();
  const [view, setView] = useState<View>('form');
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleClose = () => {
    form.resetFields();
    setView('form');
    setErrorMessage(null);
    setLoading(false);
    onClose();
  };

  const handleSubmit = async ({ email }: ForgotPasswordFormValues) => {
    setLoading(true);
    setErrorMessage(null);

    const result = await authAPI.sendPasswordResetNotice(email.trim());

    setLoading(false);

    if (result.success) {
      setView('complete');
      return;
    }

    if (result.notFound) {
      setErrorMessage('入力されたメールアドレスが見つかりませんでした。');
      return;
    }

    setErrorMessage(result.message ?? '送信に失敗しました。時間をおいて再度お試しください。');
  };

  if (view === 'complete') {
    return (
      <Modal
        title="送信完了"
        open={open}
        onCancel={handleClose}
        footer={
          <Button type="primary" onClick={handleClose}>
            閉じる
          </Button>
        }
      >
        <p style={{ margin: '16px 0' }}>
          入力いただいたメールアドレスに再設定用のリンクを送信しました。メールをご確認ください。
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="パスワードを忘れた方へ"
      open={open}
      onCancel={handleClose}
      footer={null}
    >
      <p style={{ margin: '16px 0' }}>ご登録されているメールアドレスを入力してください。</p>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="email"
          rules={[
            { required: true, message: 'メールアドレスを入力してください' },
            { type: 'email', message: '正しいメールアドレスを入力してください' },
          ]}
        >
          <Input placeholder="メールアドレス" size="large" />
        </Form.Item>
        {errorMessage && (
          <div style={{ color: '#d32f2f', fontSize: '14px', marginTop: '-16px', marginBottom: '16px' }}>
            {errorMessage}
          </div>
        )}
        <Form.Item style={{ marginBottom: 0 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={handleClose} disabled={loading}>
              キャンセル
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              送信
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ForgotPasswordModal;
